import type { ServiceTicket } from '@avrahami/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Alert, Badge, Button, Card, formatDateTime, Loading, StatusBadge } from '@avrahami/ui';
import { keys, platformApi, tenantServiceQuery } from '../api/platform';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import { openCounts, SEVERITY_TONES, STATUS_TONES, ticketTitle } from '../lib/service';

/** How many open tickets the card on the store's page lists by name. */
const CARD_LIST = 5;

/** A ticket's kind, how serious it is and its status, as badges. */
export function TicketBadges({
  ticket,
}: {
  ticket: Pick<ServiceTicket, 'kind' | 'severity' | 'status' | 'source'>;
}) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <StatusBadge value={ticket.status} labels={he.service.statuses} tones={STATUS_TONES} />
      <StatusBadge value={ticket.severity} labels={he.service.severities} tones={SEVERITY_TONES} />
      <Badge tone={ticket.kind === 'SECURITY' ? 'red' : 'slate'}>
        {he.service.kinds[ticket.kind]}
      </Badge>
      {ticket.source === 'STORE' ? <Badge tone="blue">{he.service.sources.STORE}</Badge> : null}
    </span>
  );
}

/** Runs the scan of one store now, as the hourly scan would. */
export function ScanNowButton({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const scan = useMutation({
    mutationFn: () => platformApi.scanStore(tenantId),
    onSuccess: async (list) => {
      queryClient.setQueryData(keys.tenantServiceList(tenantId), list);
      await queryClient.invalidateQueries({ queryKey: keys.serviceEverywhere });
      await queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
    },
  });
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-2">
        <Button
          tone="secondary"
          busy={scan.isPending}
          data-testid="service-scan-now"
          onClick={() => {
            scan.mutate();
          }}
        >
          {he.service.scanNow}
        </Button>
        {scan.isSuccess ? (
          <span role="status" className="text-xs text-green-800">
            {he.service.scanned}
          </span>
        ) : null}
      </div>
      {scan.isError ? <Alert>{errorMessage(scan.error)}</Alert> : null}
    </div>
  );
}

/**
 * The store's faults and service calls at a glance (adr/0036): the open ones by group, the most
 * serious by name, and when the scan last looked.
 */
export function TenantServiceCard({
  tenantId,
  canManage,
}: {
  tenantId: string;
  canManage: boolean;
}) {
  const list = useQuery(tenantServiceQuery(tenantId));
  const counts = list.data === undefined ? null : openCounts(list.data.tickets);
  const open = list.data?.tickets.filter((ticket) => ticket.status !== 'RESOLVED') ?? [];
  return (
    <Card title={he.service.title}>
      {list.isPending ? <Loading /> : null}
      {list.isError ? <Alert>{errorMessage(list.error)}</Alert> : null}
      {list.data === undefined || counts === null ? null : (
        <div className="flex flex-col gap-3 text-sm" data-testid="tenant-service">
          <dl className="grid grid-cols-3 gap-2 text-center">
            {(
              [
                ['security', he.service.openSecurity, counts.security],
                ['general', he.service.openGeneral, counts.general],
                ['calls', he.service.openCalls, counts.calls],
              ] as const
            ).map(([key, label, value]) => (
              <div
                key={key}
                className={`flex flex-col-reverse rounded-md px-2 py-2 ring-1 ${
                  value > 0 ? 'bg-red-50 ring-red-200' : 'bg-slate-50 ring-slate-200'
                }`}
              >
                <dt className="text-xs text-slate-600">{label}</dt>
                <dd
                  className={`text-xl font-bold ${value > 0 ? 'text-red-700' : 'text-slate-500'}`}
                  data-testid={`service-count-${key}`}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          {open.length === 0 ? (
            <p className="text-slate-600">{he.service.none}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {open.slice(0, CARD_LIST).map((ticket) => (
                <li key={ticket.id} className="flex flex-col gap-1">
                  <Link
                    to="/tenants/$tenantId/service"
                    params={{ tenantId }}
                    search={{ ticket: ticket.id }}
                    className="font-medium text-slate-800 hover:text-brand-700 hover:underline"
                  >
                    {ticketTitle(ticket)}
                  </Link>
                  <TicketBadges ticket={ticket} />
                </li>
              ))}
              {open.length > CARD_LIST ? (
                <li className="text-xs text-slate-500">
                  {he.service.moreOpen(open.length - CARD_LIST)}
                </li>
              ) : null}
            </ul>
          )}
          <p className="text-xs text-slate-500" data-testid="service-last-scan">
            {list.data.scannedAt === null
              ? he.service.neverScanned
              : `${he.service.lastScan}: ${formatDateTime(list.data.scannedAt)}`}
          </p>
          {canManage ? <ScanNowButton tenantId={tenantId} /> : null}
        </div>
      )}
      <Link
        to="/tenants/$tenantId/service"
        params={{ tenantId }}
        search={{}}
        data-testid="tenant-service-link"
        className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline"
      >
        {he.service.manage}
      </Link>
    </Card>
  );
}
